import { z } from 'zod';

/**
 * L'environnement, lu une fois au démarrage.
 *
 * Deux façons de tourner :
 * - `supabase` : le vrai projet, adresse et clé publique obligatoires ;
 * - `demo` : l'accès libre — des comptes fictifs et des données gardées dans le
 *   navigateur, pour voir l'application avancer sans projet Supabase.
 *
 * `npm run dev:demo` force l'accès libre. `npm run dev` s'y rabat tant que
 * `.env.local` ne décrit pas de projet. Un build de production, lui, exige
 * Supabase : il ne se rabat jamais.
 */
const schemaSupabase = z.object({
  mode: z.literal('supabase'),
  supabaseUrl: z.url({ message: 'VITE_SUPABASE_URL doit être une adresse https://….supabase.co' }),
  supabaseAnonKey: z
    .string()
    .min(20, { message: 'VITE_SUPABASE_ANON_KEY est absente ou tronquée' }),
});

const schemaDemo = z.object({ mode: z.literal('demo') });

export type Environnement = z.infer<typeof schemaSupabase> | z.infer<typeof schemaDemo>;

export type LectureEnvironnement =
  { ok: true; env: Environnement } | { ok: false; erreurs: string[] };

export function lireEnvironnement(source: {
  MODE?: string | undefined;
  VITE_SUPABASE_URL?: string | undefined;
  VITE_SUPABASE_ANON_KEY?: string | undefined;
}): LectureEnvironnement {
  const sansProjet = !source.VITE_SUPABASE_URL && !source.VITE_SUPABASE_ANON_KEY;
  if (source.MODE === 'demo' || (source.MODE === 'development' && sansProjet)) {
    return { ok: true, env: schemaDemo.parse({ mode: 'demo' }) };
  }

  const resultat = schemaSupabase.safeParse({
    mode: 'supabase',
    supabaseUrl: source.VITE_SUPABASE_URL,
    supabaseAnonKey: source.VITE_SUPABASE_ANON_KEY,
  });
  if (resultat.success) return { ok: true, env: resultat.data };
  return { ok: false, erreurs: resultat.error.issues.map((i) => i.message) };
}

export const environnement = lireEnvironnement({
  MODE: import.meta.env.MODE,
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
});
